"use client"

import { useState, useEffect } from "react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Field, FieldDescription, FieldGroup, FieldLabel, FieldSeparator } from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { createClient } from "@/lib/supabase/client"
import { toast } from "sonner"
import Link from "next/link"
import { useRouter } from "next/navigation"
import { startAuthentication } from "@simplewebauthn/browser"

export function LoginForm04({ className, ...props }: React.ComponentProps<"div">) {
  const [email, setEmail] = useState("")
  const [otp, setOtp] = useState("")
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState("")
  const [otpSent, setOtpSent] = useState(false)
  const router = useRouter()
  const supabase = createClient()

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const error = params.get("error"); const info = params.get("info")
    if (error) setMessage(error); else if (info) setMessage(info)
  }, [])

  const handlePasskeySignIn = async () => {
    setLoading(true); setMessage("")
    try {
      const optRes = await fetch("/api/auth/webauthn/authenticate-options")
      if (!optRes.ok) throw new Error((await optRes.json()).error || "Nessuna passkey disponibile")
      const credential = await startAuthentication({ optionsJSON: await optRes.json() })
      const verRes = await fetch("/api/auth/webauthn/authenticate-verify", {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(credential)
      })
      if (!verRes.ok) throw new Error((await verRes.json()).error || "Autenticazione fallita")
      const data = await verRes.json()
      if (!data.access_token || !data.refresh_token) throw new Error("Tokens mancanti")
      const { error: se } = await supabase.auth.setSession({ access_token: data.access_token, refresh_token: data.refresh_token })
      if (se) throw new Error("Impossibile creare la sessione")
      toast.success("Accesso effettuato!"); await new Promise(r => setTimeout(r, 500))
      window.location.href = "/dashboard"
    } catch (err: unknown) {
      const e = err as { name?: string; message?: string }
      if (e.name === "NotAllowedError") setMessage("Autenticazione annullata.")
      else { setMessage(e.message || "Errore passkey"); toast.error(e.message || "Errore passkey") }
    } finally { setLoading(false) }
  }

  const handleSendOtp = async () => {
    if (!email) { toast.error("Inserisci la tua email"); return }
    setLoading(true); setMessage("")
    try {
      const res = await fetch("/api/magic-link", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email }) })
      const data = await res.json()
      if (!res.ok) {
        if (data.requiresSignup) { toast.info("Account non trovato. Reindirizzamento..."); setTimeout(() => router.push(`/auth/signup?email=${encodeURIComponent(email)}`), 1500); return }
        throw new Error(data.error || "Errore durante la verifica")
      }
      if (data.canSendMagicLink) {
        const { error } = await supabase.auth.signInWithOtp({ email, options: { shouldCreateUser: false } })
        if (error) throw error
        setOtpSent(true); toast.success("Codice inviato! Controlla la tua email.")
      }
    } catch (err: unknown) {
      const e = err as { message?: string }
      setMessage(e.message || "Errore"); toast.error(e.message || "Errore")
    } finally { setLoading(false) }
  }

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 8) { toast.error("Inserisci il codice a 8 cifre"); return }
    setLoading(true); setMessage("")
    try {
      const { data, error } = await supabase.auth.verifyOtp({ email, token: otp, type: "email" })
      if (error) throw error
      if (data.user) {
        await supabase.from("users").upsert({ id: data.user.id, email: data.user.email, name: data.user.email?.split("@")[0] || "User", email_verified: new Date().toISOString() }, { onConflict: "id" })
      }
      toast.success("Accesso effettuato!"); await new Promise(r => setTimeout(r, 500))
      window.location.href = "/dashboard"
    } catch (err: unknown) {
      const e = err as { message?: string }
      setMessage(e.message || "Codice non valido"); toast.error(e.message || "Codice non valido")
    } finally { setLoading(false) }
  }

  return (
    <div className={cn("flex flex-col gap-6", className)} {...props}>
      <Card className="overflow-hidden p-0">
        <CardContent className="grid p-0 md:grid-cols-2">
          {/* Left: form */}
          <div className="p-6 md:p-8">
            <div className="flex flex-col gap-6">
              <div className="flex flex-col items-center text-center">
                <h1 className="text-2xl font-bold">Bentornato</h1>
                <p className="text-muted-foreground text-sm text-balance">Accedi con passkey o codice email</p>
              </div>
              {message && <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">{message}</div>}
              {otpSent ? (
                <FieldGroup>
                  <div className="text-center text-sm text-muted-foreground">Codice inviato a <strong>{email}</strong></div>
                  <Field>
                    <FieldLabel htmlFor="otp-04">Codice OTP (8 cifre)</FieldLabel>
                    <Input id="otp-04" type="text" inputMode="numeric" maxLength={8} value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, ""))} onKeyDown={e => e.key === "Enter" && handleVerifyOtp()} placeholder="00000000" className="text-center tracking-widest text-lg" autoFocus />
                  </Field>
                  <Field><Button type="button" onClick={handleVerifyOtp} disabled={loading || otp.length !== 8}>{loading ? "Verifica..." : "Verifica e Accedi"}</Button></Field>
                  <div className="flex justify-between text-sm">
                    <button type="button" onClick={() => { setOtpSent(false); setOtp(""); setMessage("") }} className="text-primary hover:underline">← Cambia email</button>
                    <button type="button" onClick={handleSendOtp} disabled={loading} className="text-muted-foreground hover:underline">Nuovo codice</button>
                  </div>
                </FieldGroup>
              ) : (
                <FieldGroup>
                  <Field>
                    <Button type="button" variant="outline" onClick={handlePasskeySignIn} disabled={loading}>
                      <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" /></svg>
                      {loading ? "Caricamento..." : "Accedi con Passkey"}
                    </Button>
                  </Field>
                  <FieldSeparator className="*:data-[slot=field-separator-content]:bg-card">Oppure con email</FieldSeparator>
                  <Field>
                    <FieldLabel htmlFor="email-04">Email</FieldLabel>
                    <Input id="email-04" type="email" placeholder="tua@email.com" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSendOtp()} required />
                  </Field>
                  <Field>
                    <Button type="button" onClick={handleSendOtp} disabled={loading || !email}>{loading ? "Invio..." : "Invia Codice OTP"}</Button>
                    <FieldDescription className="text-center">Non hai un account? <Link href="/auth/signup" className="underline underline-offset-4">Registrati</Link></FieldDescription>
                  </Field>
                </FieldGroup>
              )}
            </div>
          </div>
          {/* Right: decorative panel */}
          <div className="bg-gradient-to-br from-primary to-primary/70 hidden md:flex flex-col items-center justify-center gap-4 p-8 text-primary-foreground">
            <div className="flex items-center justify-center h-20 w-20 rounded-full bg-primary-foreground/20 backdrop-blur-sm">
              <svg className="h-10 w-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
            </div>
            <div className="text-center">
              <h2 className="text-xl font-bold">PadelManager</h2>
              <p className="text-sm text-primary-foreground/80 mt-1">Gestisci tornei e partite di padel in modo semplice e intuitivo.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
